import { memo } from 'react';
import { toast } from 'sonner';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { useSectionDefaults } from '@/hooks/use-section-defaults';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { getCustomSectionById, isBuiltInReportSectionId } from '@/lib/report-sections';
import { isCommentableSectionId } from '@/pages/create-report/comments/utils';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import { TestBlock } from '@/types/report-payload';
import { SECTIONS } from '../wizard/constants';
import { Sections } from '../wizard/types';
import EditableSection from './EditableSection';
import TestSectionRenderer from './TestSectionRenderer';
import type { ReportFootnotePlan } from './footnotes';
import type { EditingTextSection, ReportData, Section } from './types';

const TEXT_SECTION_IDS: Sections[] = [
    SECTIONS.DEMAND_DESCRIPTION,
    SECTIONS.PROCEDURES,
    SECTIONS.BEHAVIOR_OBSERVATIONS,
    SECTIONS.CLINICAL_HISTORY,
    SECTIONS.DIAGNOSIS,
    SECTIONS.RECOMMENDATIONS,
];

interface SectionContentProps {
    section: Section;
    data: ReportData;
    onUpdate: (updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>)) => void;
    disabled?: boolean;
    onAddTest: (blockId?: string, mode?: 'default' | 'inline') => void;
    /**
     * Only the tests section's empty state uses it, for the "Instrumento Livre"
     * half of its menu — the Satepsi half stays on `onAddTest`. Optional so the
     * hidden measurer, which never renders that CTA, needs no no-op for it.
     */
    onAddBlock?: (toolId: string) => void;

    onAddChartFromTable: (blockId: string) => void;
    onEditChart: (block: TestBlock) => void;
    handleDeleteRequest: (sectionId: string) => void;
    isEditingSection?: boolean;
    /**
     * True on every fragment of the section being edited (matched by section id,
     * not render key), so a section split across pages dims as a whole.
     */
    isSectionEditing?: boolean;
    onEditingSectionChange?: (section: EditingTextSection) => void;
    renderKey?: string;
    measureBlocks?: boolean;
    enableDragAndDrop?: boolean;
    droppableKey?: string;
    contentOverride?: string;
    blocksOverride?: TestBlock[];
    blockStartIndex?: number;
    editingBlockId?: string | number | null;
    onEditingBlockChange?: (blockId: string | number | null) => void;
    footnotePlan?: ReportFootnotePlan;
}

function SectionContentComponent({
    section,
    data,
    onUpdate,
    disabled,
    onAddTest,
    onAddBlock,
    onAddChartFromTable,
    onEditChart,
    handleDeleteRequest,
    isEditingSection,
    isSectionEditing,
    onEditingSectionChange,
    renderKey,
    measureBlocks,
    enableDragAndDrop,
    droppableKey,
    contentOverride,
    blocksOverride,
    blockStartIndex,
    editingBlockId,
    onEditingBlockChange,
    footnotePlan,
}: SectionContentProps) {
    const { silentSave, requestSectionComment } = useReportWizard();
    const sectionDefaultsAccess = useFeatureEntitlement('sectionDefaults.save');
    // Reads the shared `['entitlements']` query cache (30s staleTime), so the
    // context menu can show the Plano+ chip without a request per section.
    const aiAccess = useFeatureEntitlement('ai.credits');
    const isTestSection = section.id === SECTIONS.TESTS;

    const isBuiltInSection = isBuiltInReportSectionId(section.id);
    const isTextSection = TEXT_SECTION_IDS.includes(section.id as Sections);

    const { defaultContent, updateDefault, isUpdating } = useSectionDefaults(section.id, {
        enabled: !data.id && isTextSection,
    });

    // Determine content to render: override (split part) takes precedence over full data
    const sectionData = isBuiltInSection
        ? (data[section.id as keyof ReportData] as { content?: string } | undefined)
        : getCustomSectionById(data, section.id);
    const contentToRender = contentOverride !== undefined ? contentOverride : sectionData?.content;
    const fullContent = sectionData?.content ?? contentToRender;

    const isDefault = isTextSection && fullContent === defaultContent;
    const blockedDefaultMessage = getEntitlementBlockedMessage(sectionDefaultsAccess.decision);

    return (
        <>
            {isTestSection && (
                <TestSectionRenderer
                    data={data}
                    onUpdate={onUpdate}
                    disabled={disabled}
                    onAddTest={(id, mode) => onAddTest(id?.toString(), mode)}
                    onAddBlock={onAddBlock}
                    onAddChartFromTable={onAddChartFromTable}
                    onEditChart={onEditChart}
                    measureBlocks={measureBlocks}
                    enableDragAndDrop={enableDragAndDrop}
                    droppableKey={droppableKey}
                    blocksOverride={blocksOverride}
                    blockStartIndex={blockStartIndex}
                    editingBlockId={editingBlockId}
                    onEditingBlockChange={onEditingBlockChange}
                    footnotePlan={footnotePlan}
                />
            )}

            {!isTestSection && (
                <EditableSection
                    content={contentToRender}
                    defaultEditValue={fullContent}
                    copyContent={fullContent}
                    onSave={(value) => {
                        onUpdate((prev) => {
                            if (!isBuiltInReportSectionId(section.id)) {
                                return {
                                    custom_sections: (prev.custom_sections ?? []).map(
                                        (customSection) =>
                                            customSection.id === section.id
                                                ? { ...customSection, content: value }
                                                : customSection,
                                    ),
                                };
                            }

                            const currentData = prev[section.id as keyof ReportData];
                            const newData =
                                typeof currentData === 'object' && currentData !== null
                                    ? { ...currentData, content: value }
                                    : { content: value };
                            return { [section.id]: newData } as Partial<ReportData>;
                        });

                        // Show toast with action if content is different from default
                        if (isTextSection && value && value !== defaultContent) {
                            toast.success('Seção salva com sucesso', {
                                action: sectionDefaultsAccess.shouldBlock
                                    ? undefined
                                    : {
                                          label: 'Definir como padrão',
                                          onClick: () => updateDefault(value),
                                      },
                            });
                        }
                    }}
                    onDelete={isBuiltInSection ? () => handleDeleteRequest(section.id) : undefined}
                    placeholder={`Clique para adicionar ${section.title.toLowerCase()}...`}
                    disabled={disabled}
                    isEditing={isEditingSection}
                    isContentDimmed={isSectionEditing}
                    onEditingChange={(isEditing) =>
                        onEditingSectionChange?.(
                            isEditing
                                ? {
                                      sectionId: section.id,
                                      renderKey: renderKey ?? section.id,
                                  }
                                : null,
                        )
                    }
                    isDefault={isDefault}
                    onSaveAsDefault={
                        isTextSection ? () => updateDefault(fullContent || '') : undefined
                    }
                    onBlockedSaveAsDefault={() => {
                        toast.warning(blockedDefaultMessage);
                    }}
                    isSaveAsDefaultBlocked={sectionDefaultsAccess.shouldBlock}
                    saveAsDefaultBlockedMessage={blockedDefaultMessage}
                    saveAsDefaultAvailablePlan={
                        sectionDefaultsAccess.isUnavailableInPlan &&
                        sectionDefaultsAccess.availableFromPlan
                            ? sectionDefaultsAccess.availableFromPlan
                            : null
                    }
                    isUpdatingDefault={isUpdating}
                    reportId={data.id}
                    sectionId={section.id}
                    onBeforeGenerate={silentSave}
                    onComment={
                        data.id && isCommentableSectionId(section.id)
                            ? () => requestSectionComment(section.id)
                            : undefined
                    }
                    isAIBlocked={aiAccess.shouldBlock}
                    aiAvailablePlan={
                        aiAccess.isUnavailableInPlan && aiAccess.availableFromPlan
                            ? aiAccess.availableFromPlan
                            : null
                    }
                />
            )}
        </>
    );
}

const SectionContent = memo(SectionContentComponent);

export default SectionContent;
